'use client';

import React, { useEffect, useState } from 'react';
import ReviewsSection from './ReviewsSection';
import ReviewForm from './ReviewForm';
import CommunityPhotos from './CommunityPhotos';
import CommunityPhotoUpload from './CommunityPhotoUpload';
import VisitorInsights from './VisitorInsights';

interface EntityReviewsProps {
  entityId: number;
  entityName: string;
}

/**
 * Client-side wrapper that ties together the ReviewsSection, ReviewForm,
 * CommunityPhotos, and CommunityPhotoUpload components.
 *
 * The child sections (CommunityPhotos, ReviewsSection) hide themselves
 * when empty — the sidebar SidebarReviewCTAs component provides the
 * upload / write-review CTAs instead, communicating via custom DOM events.
 */
export default function EntityReviews({ entityId, entityName }: EntityReviewsProps) {
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Listen for sidebar CTA custom events
  useEffect(() => {
    const openReview = () => setReviewFormOpen(true);
    const openPhoto = () => setPhotoUploadOpen(true);
    // Fired by ReviewerAuthContext when a pending review auto-submits after an
    // OAuth sign-in — refresh so counts/state stay current on this campsite.
    const onReviewSubmitted = (e: Event) => {
      const id = (e as CustomEvent<{ entityId?: number }>).detail?.entityId;
      if (id == null || id === entityId) setRefreshKey((k) => k + 1);
    };
    window.addEventListener('open-review-form', openReview);
    window.addEventListener('open-photo-upload', openPhoto);
    window.addEventListener('review-submitted', onReviewSubmitted);
    return () => {
      window.removeEventListener('open-review-form', openReview);
      window.removeEventListener('open-photo-upload', openPhoto);
      window.removeEventListener('review-submitted', onReviewSubmitted);
    };
  }, [entityId]);

  const handleReviewSuccess = () => {
    // Force ReviewsSection to re-fetch by changing its key
    setRefreshKey((k) => k + 1);
  };

  const handlePhotoUploadSuccess = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <>
      {/* Community Photos Section */}
      <CommunityPhotos
        key={`photos-${refreshKey}`}
        entityId={entityId}
        entityName={entityName}
        onUploadClick={() => setPhotoUploadOpen(true)}
      />

      {/* Visitor insights — summarised from visitor videos, owner-approved */}
      <VisitorInsights key={`insights-${refreshKey}`} entityId={entityId} />

      {/* Reviews Section */}
      <ReviewsSection
        key={`reviews-${refreshKey}`}
        entityId={entityId}
        entityName={entityName}
        onWriteReview={() => setReviewFormOpen(true)}
      />

      {/* Review Form Drawer */}
      <ReviewForm
        entityId={entityId}
        entityName={entityName}
        isOpen={reviewFormOpen}
        onClose={() => setReviewFormOpen(false)}
        onSubmitSuccess={handleReviewSuccess}
      />

      {/* Photo Upload Modal */}
      <CommunityPhotoUpload
        entityId={entityId}
        entityName={entityName}
        isOpen={photoUploadOpen}
        onClose={() => setPhotoUploadOpen(false)}
        onUploadSuccess={handlePhotoUploadSuccess}
      />
    </>
  );
}
